import { useState, useEffect, useRef } from 'react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { useWallet } from '@baaligames/shared-ui/lib/wallet';

export interface BetSelection {
  matchId: string;
  marketId: string;
  runnerName: string;
  price: string;
  type: 'back' | 'lay';
  // Which market this pick came from — selects the right per-market cap below.
  marketType?: string;
  // Player-selectable counterparty. `allowedModes` is what THIS market offers
  // (['house'] | ['p2p'] | ['house','p2p']); when it has both, the slip shows a
  // House/Exchange selector. `mode` is the initial pick (the market default).
  // `pricesByMode` gives THIS side's odds for each mode, so switching mode on the
  // slip re-prices instantly with no round-trip (House = margined, Exchange = raw).
  allowedModes?: string[];
  mode?: string;
  pricesByMode?: Record<string, string>;
}

/** The player's REAL bet bounds, from GET /cricket/my-limits (engine truth). */
export interface CricketLimits {
  min: number;
  max: number;
  perMarket: Record<string, number>;
  maxMatchExposure: number | null;
  personal: boolean;
  /** Exchange (P2P) commission the server deducts from a WINNER'S PROFIT at
   *  settlement, as a percent. null = the server did not tell us (older endpoint,
   *  or the fetch failed) — then the slip says a commission applies but never
   *  invents a rate. */
  commissionPct: number | null;
}

interface BetSlipProps {
  selection: BetSelection | null;
  onClose: () => void;
  onPlaceBet: (stake: number, mode: string) => void;
  limits?: CricketLimits | null;
  /** In-play (live) match? Only then do we apply the 5s confirm hold. */
  inPlay?: boolean;
}

// The last stake the player actually placed, kept for the session so the
// next slip pre-fills it — repeat bets become one tap (2026-08-06 UX sweep).
let rememberedStake = '';
// Auto-close countdown length (seconds). Any interaction with the slip
// resets it, so it never closes while the player is typing or tapping.
const AUTO_CLOSE_SECS = 12;

export default function BetSlip({ selection, onClose, onPlaceBet, limits, inPlay }: BetSlipProps) {
  const { balance } = useWallet();
  const [stakeStr, setStakeStr] = useState<string>('100');
  const [timeLeft, setTimeLeft] = useState<number>(AUTO_CLOSE_SECS);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  // The counterparty the player is betting against for THIS bet. Seeded from the
  // market's default; the player can flip it below when the market offers both.
  const [mode, setMode] = useState<string>('house');
  // Set the instant a placement starts. The auto-close countdown FREEZES while
  // this is true so a bet in flight can never be yanked out from under itself —
  // see the countdown effect. A ref (not state) so the interval closure reads the
  // live value without being re-created.
  const placingRef = useRef(false);

  // 2026-07-16 FIX. These were hardcoded 100 / 200,000 while the engine enforced
  // 10 / 100,000 — so a legal ₹50 was blocked right here, and an illegal ₹150,000
  // sailed through the slip only to be rejected by the server. They now come from
  // /my-limits, which reports exactly what the engine enforces, including any
  // personal cap the player's agent set (previously invisible — a player only
  // discovered it by having a bet rejected). Fallbacks match the SERVER defaults,
  // so even before the fetch lands the slip is honest.
  const MIN_AMOUNT = limits?.min ?? 10;
  const marketCap = selection?.marketType ? limits?.perMarket?.[selection.marketType] : undefined;
  const MAX_AMOUNT = marketCap ?? limits?.max ?? 100000;
  // True when this player's own cap is what's binding (not the house-wide max).
  const capIsPersonal = !!limits?.personal && marketCap != null && marketCap < (limits?.max ?? Infinity);

  // Auto-close countdown — runs ONLY while the slip is open (selection set).
  // BUG FIX (2026-07-05): this component stays mounted and returns null when
  // closed, so the old countdown effect kept running while closed, ticking
  // timeLeft to 0 and then calling onClose() on every render. The moment the
  // user opened the slip, that stale timeLeft<=0 fired onClose() and slammed
  // it shut instantly — no bet slip could ever stay open. Now the countdown
  // is created on open, reset each open, and torn down on close.
  useEffect(() => {
    if (!selection) return;                 // closed → no countdown, no auto-close
    // Pre-fill the last stake the player placed (clamped to this market's
    // bounds), so a repeat bet is a single tap. Falls back to the minimum.
    const remembered = parseFloat(rememberedStake);
    setStakeStr(
      Number.isFinite(remembered) && remembered >= MIN_AMOUNT
        ? String(Math.min(remembered, MAX_AMOUNT))
        : MIN_AMOUNT.toString(),
    );
    setMode(selection.mode ?? selection.allowedModes?.[0] ?? 'house');  // reset counterparty on open
    setTimeLeft(AUTO_CLOSE_SECS);            // reset the countdown on open
    placingRef.current = false;              // fresh open → not placing
    const timer = setInterval(() => {
      // FREEZE while a bet is being placed. handlePlaceBet holds for ~5s before it
      // calls onPlaceBet; without this the 8s timer would auto-close the slip
      // mid-flight, and the parent's `if (!selection) return` would then silently
      // DROP the confirmed bet (you'd see PROCESSING… then nothing). Holding the
      // countdown keeps `selection` alive until the placement actually fires.
      if (placingRef.current) return;
      setTimeLeft((prev) => {
        if (prev <= 1) { onClose(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
    // onClose intentionally omitted from deps: it changes identity every parent
    // render but always does setSelection(null); including it would reset the
    // timer on every tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection]);

  if (!selection) return null;

  // Which modes this market offers, and whether to show the House/Exchange picker.
  const allowedModes = selection.allowedModes?.length ? selection.allowedModes : ['house'];
  const showModePicker = allowedModes.length > 1;
  // The active mode must be one this market allows (guards a stale seed).
  const activeMode = allowedModes.includes(mode) ? mode : allowedModes[0];
  // Odds for the ACTIVE mode: House = margined price, Exchange = raw price. Falls
  // back to the static price the board showed if a per-mode price is missing.
  const modePrice = selection.pricesByMode?.[activeMode];
  const priceStr = modePrice != null && modePrice !== '' ? modePrice : selection.price;

  const stake = parseFloat(stakeStr) || 0;
  const odds = parseFloat(priceStr) || 0;

  const isBack = selection.type === 'back';

  // Profit / liability — MUST mirror the server (engine.js profitIfWin / atRiskAmount)
  // or the preview lies. Bookmaker & Session (fancy) odds are PER-100 "bhav"
  // (a rate of 52 = bet 100 to win 52); Match Odds are decimal (1.81 = ×1.81).
  // Feeding a per-100 rate into decimal math was the "₹52 shows as ₹5100" bug.
  const perHundred = selection.marketType === 'bookmaker' || selection.marketType === 'fancy';
  const s = Math.floor(stake) || 0;   // engine floors the stake before settling
  const winSide = perHundred ? Math.round(s * odds / 100) : Math.round(s * (odds - 1));
  let liability = 0;
  let profit = 0;
  if (isBack) {
    liability = s;          // a backer risks only the stake
    profit = winSide;       // …to win the odds-derived amount
  } else {
    liability = winSide;    // a layer risks the liability…
    profit = s;             // …to win the backer's stake
  }

  // EXCHANGE COMMISSION — the slip used to show `profit` as "To Win" and stop
  // there, while the server took a cut of exactly this number before the wallet
  // ever saw it (settleP2PMatchedBet in shared/src/games/cricket/model.js): a
  // ₹1,000 back at 2.00 read ₹1,000 To Win and paid ₹900. Mirror of the server
  // rule, clause for clause — if any of these three drifts the slip lies again:
  //   • EXCHANGE (p2p) ONLY. Sportsbook (house) settlement charges no commission;
  //     the house edge there is already inside the offered price, so charging it
  //     here too would understate that payout by the same 10%.
  //   • On the PROFIT, never on the stake, and only while that profit is positive
  //     — a losing bet and a voided bet are never touched.
  //   • ONE Math.round of (pct/100 × profit), per bet, exactly as the server rounds.
  // The rate is not guessable, so an unknown rate is disclosed as words, not as a
  // number (see the review box below) — never silently treated as zero.
  const commissionPct = activeMode === 'p2p' ? limits?.commissionPct ?? null : 0;
  const commission = commissionPct != null && commissionPct > 0 && profit > 0
    ? Math.round((commissionPct / 100) * profit)
    : 0;
  // What actually lands in the wallet as winnings once the house has taken its cut.
  const netProfit = profit - commission;
  // An Exchange bet whose commission rate we could not read: the gross is still
  // true, but it is not the take-home, and the slip must say so.
  const commissionUnknown = activeMode === 'p2p' && commissionPct == null;

  // Format as Indian Rupee without decimals
  const formattedLiability = Math.round(liability).toLocaleString('en-IN');
  const formattedProfit = Math.round(profit).toLocaleString('en-IN');
  const formattedNetProfit = Math.round(netProfit).toLocaleString('en-IN');
  const formattedCommission = Math.round(commission).toLocaleString('en-IN');

  const canAfford = balance >= liability;

  const handleQuickStake = (amount: number) => {
    setStakeStr((prev) => {
      const current = parseFloat(prev) || 0;
      return (current + amount).toString();
    });
  };

  const handlePlaceBet = async () => {
    if (stake >= MIN_AMOUNT && stake <= MAX_AMOUNT && canAfford && timeLeft > 0) {
      placingRef.current = true;   // freeze the auto-close countdown until this resolves
      setIsProcessing(true);
      rememberedStake = String(stake);   // pre-fill this amount on the next slip
      // 5-second confirm hold — ONLY for in-play (live) markets (the industry
      // norm, to absorb live odds-flicker mid-ball). Pre-match bets place
      // immediately; the hold was pure friction there.
      if (inPlay) await new Promise(resolve => setTimeout(resolve, 5000));
      await onPlaceBet(stake, activeMode);
      setIsProcessing(false);
      placingRef.current = false;
    }
  };

  const chips = [
    { label: '+100', val: 100 },
    { label: '+200', val: 200 },
    { label: '+500', val: 500 },
    { label: '+1K', val: 1000 },
    { label: '+2K', val: 2000 },
    { label: '+5K', val: 5000 },
    { label: '+10K', val: 10000 },
    { label: '+25K', val: 25000 },
    { label: '+50K', val: 50000 },
  ];

  return (
    <>
      <div 
        className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      
      <div
        onPointerDownCapture={() => setTimeLeft(AUTO_CLOSE_SECS)}
        onInput={() => setTimeLeft(AUTO_CLOSE_SECS)}
        className={`fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-arena-900 rounded-t-[32px] shadow-[0_-10px_40px_rgba(0,0,0,0.3)] transition-transform duration-300 pb-safe`}>
        
        {/* Header */}
        <div className={`px-5 py-5 border-b border-arena-100 dark:border-white/5 flex justify-between items-center rounded-t-[32px] ${isBack ? 'bg-blue-50 dark:bg-blue-900/30' : 'bg-pink-50 dark:bg-pink-900/30'}`}>
          <div className="flex flex-col">
            <span className={`text-[11px] font-black uppercase tracking-wider mb-0.5 ${isBack ? 'text-blue-600 dark:text-blue-400' : 'text-pink-600 dark:text-pink-400'}`}>
              {isBack ? 'Bet FOR (Back)' : 'Bet AGAINST (Lay)'}
            </span>
            <span className="font-black text-2xl text-arena-900 dark:text-white">{selection.runnerName}</span>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Timer Pill */}
            <div className={`px-3 py-1.5 rounded-full flex items-center gap-1.5 font-bold text-xs shadow-sm ${
              timeLeft <= 3 ? 'bg-red-500 text-white animate-pulse' : 'bg-white dark:bg-white/10 text-arena-900 dark:text-white'
            }`}>
              <Icon name="timer" size={14} />
              {timeLeft}s
            </div>

            <button onClick={onClose} className="w-10 h-10 flex items-center justify-center bg-white/50 dark:bg-white/10 rounded-full hover:bg-black/5 dark:hover:bg-white/20 active:scale-95 transition">
              <Icon name="close" size={24} className="text-arena-900 dark:text-white" />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-5 max-w-3xl mx-auto">

          {/* Counterparty picker — only when this market offers BOTH. Industry-standard
              labels (Betfair-style): EXCHANGE = bet against other players; SPORTSBOOK =
              fixed odds against the house. Exchange is listed FIRST and pre-selected —
              the exchange is our lead product. Odds + risk/win below re-price on flip. */}
          {showModePicker && (
            <div>
              <div className="grid grid-cols-2 gap-2 p-1 bg-arena-100 dark:bg-arena-800 rounded-2xl">
                {[
                  { key: 'p2p', label: 'Exchange', sub: 'vs players · true odds', icon: 'groups' },
                  { key: 'house', label: 'Sportsbook', sub: 'vs house · instant', icon: 'account_balance' },
                ].filter((o) => allowedModes.includes(o.key)).map((o) => {
                  const on = activeMode === o.key;
                  return (
                    <button key={o.key} onClick={() => setMode(o.key)}
                      className={`flex flex-col items-center justify-center py-2.5 rounded-xl transition active:scale-95 ${on ? 'bg-white dark:bg-arena-700 shadow-sm text-arena-900 dark:text-white' : 'text-arena-500 dark:text-arena-400 hover:text-arena-700 dark:hover:text-arena-200'}`}>
                      <span className="flex items-center gap-1.5 font-black text-sm uppercase tracking-wide"><Icon name={o.icon} size={16} />{o.label}</span>
                      <span className="text-[10px] font-bold opacity-70 tracking-normal">{o.sub}</span>
                    </button>
                  );
                })}
              </div>
              <div className="text-center mt-1.5 text-[10px] font-semibold text-arena-400 dark:text-arena-500 leading-snug">
                {activeMode === 'p2p'
                  // Name the rate. "A small commission" told the player nothing and
                  // left a 10% haircut to be discovered in the wallet after a win.
                  ? `Exchange — matched against other players at true market odds; ${commissionPct != null && commissionPct > 0 ? `${commissionPct}% commission` : 'a commission'} is deducted from winnings only.`
                  : 'Sportsbook — fixed odds set by the house, which takes the other side of your bet instantly. No commission.'}
              </div>
            </div>
          )}

          {/* Inputs Row */}
          <div className="flex gap-4 items-center">
            <div className="flex flex-col items-center justify-center bg-arena-50 dark:bg-arena-800 rounded-2xl border-2 border-arena-100 dark:border-white/5 p-3 w-24 shadow-inner">
              <span className="text-[10px] font-bold text-arena-500 dark:text-arena-400 uppercase tracking-widest mb-1">Odds</span>
              <span className="font-black text-2xl text-arena-900 dark:text-white">{priceStr}</span>
            </div>
            
            <div className="flex-1 relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-arena-400 font-black text-xl">₹</span>
              <input 
                type="number"
                value={stakeStr}
                onChange={(e) => setStakeStr(e.target.value)}
                placeholder="0"
                className="w-full h-16 bg-white dark:bg-arena-800 border-2 border-arena-200 dark:border-arena-600 rounded-2xl px-10 font-black text-2xl text-arena-900 dark:text-white focus:outline-none focus:border-brand-500 dark:focus:border-brand-500 transition-colors shadow-inner [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex gap-1">
                <button onClick={() => setStakeStr(s => Math.max(0, (parseFloat(s) || 0) - 100).toString())} className="w-8 h-8 rounded-lg bg-arena-100 dark:bg-arena-700 hover:bg-arena-200 dark:hover:bg-arena-600 text-arena-600 dark:text-arena-300 flex items-center justify-center transition active:scale-95">
                  <Icon name="remove" size={18} />
                </button>
                <button onClick={() => setStakeStr(s => ((parseFloat(s) || 0) + 100).toString())} className="w-8 h-8 rounded-lg bg-arena-100 dark:bg-arena-700 hover:bg-arena-200 dark:hover:bg-arena-600 text-arena-600 dark:text-arena-300 flex items-center justify-center transition active:scale-95">
                  <Icon name="add" size={18} />
                </button>
              </div>
            </div>
          </div>
          
          <div className="text-center -mt-3 text-[10px] font-bold text-arena-400 uppercase tracking-widest">
            Allowed limits: ₹{MIN_AMOUNT.toLocaleString('en-IN')} — ₹{MAX_AMOUNT.toLocaleString('en-IN')}
            {/* Say so plainly when the binding number is this player's OWN cap
                rather than the house max — they used to find out only by rejection. */}
            {capIsPersonal && <span className="text-brand-500"> · your limit</span>}
          </div>

          {/* 9 Quick Chips Grid */}
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {chips.map(chip => (
              <button 
                key={chip.val}
                onClick={() => handleQuickStake(chip.val)}
                className="py-2.5 rounded-xl text-sm font-black transition active:scale-95 border bg-arena-50 dark:bg-arena-800 border-arena-200 dark:border-white/5 text-arena-700 dark:text-arena-300 hover:bg-arena-100 dark:hover:bg-arena-700 hover:border-arena-300 dark:hover:border-white/10"
              >
                {chip.label}
              </button>
            ))}
            
            {/* Action Buttons built into the grid for compactness */}
            <button onClick={() => setStakeStr(MIN_AMOUNT.toString())} className="py-2.5 rounded-xl text-[11px] font-black uppercase tracking-wider text-brand-600 bg-brand-50 hover:bg-brand-100 dark:text-brand-400 dark:bg-brand-500/10 dark:hover:bg-brand-500/20 transition border border-brand-200 dark:border-brand-500/30 active:scale-95 col-span-1 sm:col-span-2">
              Min
            </button>
            <button onClick={() => setStakeStr(MAX_AMOUNT.toString())} className="py-2.5 rounded-xl text-[11px] font-black uppercase tracking-wider text-brand-600 bg-brand-50 hover:bg-brand-100 dark:text-brand-400 dark:bg-brand-500/10 dark:hover:bg-brand-500/20 transition border border-brand-200 dark:border-brand-500/30 active:scale-95 col-span-1 sm:col-span-2">
              Max
            </button>
            <button onClick={() => setStakeStr('')} className="py-2.5 rounded-xl text-[11px] font-black uppercase tracking-wider text-red-600 bg-red-50 hover:bg-red-100 dark:text-red-400 dark:bg-red-500/10 dark:hover:bg-red-500/20 transition border border-red-200 dark:border-red-500/30 active:scale-95 col-span-1">
              Clear
            </button>
          </div>

          {/* Math Review Box (You Risk -> To Win). "To Win" is the money that
              actually reaches the wallet: on an Exchange bet that is the profit
              AFTER commission, with the gross and the deduction spelled out
              underneath so the player can see where the difference went. */}
          <div className="bg-arena-50 dark:bg-arena-800/50 rounded-2xl p-4 border border-arena-100 dark:border-white/5">
            <div className="flex justify-between items-center">
              <div className="flex flex-col">
                <span className="text-[11px] font-bold text-arena-500 dark:text-arena-400 uppercase tracking-widest mb-1">You Risk</span>
                <span className="font-black text-xl text-red-500">₹ {formattedLiability}</span>
              </div>

              <div className="w-10 h-10 rounded-full bg-white dark:bg-arena-700 flex items-center justify-center shadow-sm">
                <Icon name="arrow_forward" size={20} className="text-brand-500" />
              </div>

              <div className="flex flex-col items-end">
                <span className="text-[11px] font-bold text-arena-500 dark:text-arena-400 uppercase tracking-widest mb-1">
                  {commission > 0 ? 'You Get' : 'To Win'}
                </span>
                <span className="font-black text-xl text-green-500">₹ {commission > 0 ? formattedNetProfit : formattedProfit}</span>
              </div>
            </div>

            {/* The derivation, only when there is something to derive. */}
            {commission > 0 && (
              <div className="mt-2 pt-2 border-t border-arena-200/70 dark:border-white/5 flex justify-between items-center text-[11px] font-bold text-arena-500 dark:text-arena-400">
                <span className="uppercase tracking-wider">Winnings ₹{formattedProfit}</span>
                <span className="tabular-nums">− {commissionPct}% commission ₹{formattedCommission}</span>
              </div>
            )}
            {commissionUnknown && profit > 0 && (
              <div className="mt-2 pt-2 border-t border-arena-200/70 dark:border-white/5 text-[11px] font-bold text-arena-500 dark:text-arena-400">
                Before commission — the Exchange deducts a commission from winnings when the bet settles.
              </div>
            )}
          </div>

          {/* Live balance line (2026-08-06 UX sweep) — the player sees what
              they have vs what they're risking, right where they decide. */}
          <div className="flex items-center justify-between px-1 -mt-2 text-[12px] font-bold">
            <span className="text-arena-500 dark:text-arena-400 uppercase tracking-wider">Your balance</span>
            <span className={`tabular-nums ${canAfford ? 'text-arena-800 dark:text-white' : 'text-red-500'}`}>
              ₹{Math.round(balance).toLocaleString('en-IN')}{!canAfford && ' · not enough'}
            </span>
          </div>

          {/* Confirm Button */}
          <button
            onClick={handlePlaceBet}
            disabled={stake < MIN_AMOUNT || stake > MAX_AMOUNT || !canAfford || timeLeft <= 0 || isProcessing}
            className={`w-full py-5 rounded-2xl font-black text-xl uppercase tracking-widest transition-all relative overflow-hidden ${
              stake < MIN_AMOUNT || stake > MAX_AMOUNT
                ? 'bg-arena-200 dark:bg-arena-800 text-arena-400 dark:text-arena-500 cursor-not-allowed'
                : !canAfford
                ? 'bg-red-500 text-white cursor-not-allowed shadow-[0_0_20px_rgba(239,68,68,0.3)]'
                : isProcessing
                ? 'bg-brand-600 text-transparent cursor-wait shadow-[0_0_30px_rgba(249,115,22,0.4)]'
                : 'bg-brand-500 hover:bg-brand-400 text-white shadow-[0_0_30px_rgba(249,115,22,0.4)] dark:shadow-[0_0_30px_rgba(249,115,22,0.2)] active:scale-[.98]'
            }`}
          >
            {isProcessing && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Icon name="progress_activity" size={28} className="text-white animate-spin" />
                <span className="ml-3 text-white font-black text-lg tracking-widest">PROCESSING...</span>
              </div>
            )}
            
            <span className={isProcessing ? 'opacity-0' : 'opacity-100'}>
              {stake < MIN_AMOUNT 
                ? `Min Stake ₹${MIN_AMOUNT}` 
                : stake > MAX_AMOUNT 
                ? `Max Stake ₹${MAX_AMOUNT}`
                : !canAfford 
                ? 'Insufficient Balance' 
                : 'Confirm Bet'}
            </span>
          </button>
          
        </div>
      </div>
    </>
  );
}
